import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { IntroductionComponent } from '../../components/introduction/introduction.component';
import { PopupMessageComponent } from '../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { Icons } from '../../../shared/helpers/icons';
import { IUserInfo } from '../../../shared/models/contact';
import { UserDataService } from '../../../shared/services/userData.service';
import { IShelterInformation } from '../../../shared/models/shelterInformation';
import { ShelterInformationService } from '../../../shared/services/shelterInformation.service';

interface IShelterField {
  key: keyof IShelterInformation;
  label: string;
  type: 'yesno' | 'text';
}

@Component({
  selector: 'shelter-information',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    IntroductionComponent,
    PopupMessageComponent,
    SpinnerComponent,
    PrvNextButtonsComponent
  ],
  templateUrl: './shelter-information.component.html',
  styleUrl: './shelter-information.component.css'
})
export class ShelterInformationComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;

  data: IShelterInformation = {};

  fields: IShelterField[] = [
    { key: 'red_cross_shelter', label: 'Is this an approved Red Cross Shelter?', type: 'yesno' },
    { key: 'special_needs_capacity', label: 'Does this school have special needs capacity?', type: 'yesno' },
    { key: 'shelter_capacity', label: 'What is the shelter capacity of this school?', type: 'text' },
    { key: 'emergency_kit_locations', label: 'Emergency Kit Locations?', type: 'text' },
    { key: 'first_aid_kit_locations', label: 'First Aid Kit Locations?', type: 'text' },
    { key: 'aed_locations', label: 'AED Locations?', type: 'text' },
  ];

  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string | null = null;

  constructor(
    private userDataService: UserDataService,
    private shelterInformationService: ShelterInformationService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.loadData();
  }

  loadData(): void {
    if (this.currentBuildingId) {
      this.loading = true;
      this.shelterInformationService.getShelterInformation(this.currentBuildingId).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.data = response.data || {};
          }
        },
        error: (err) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      });
    }
  }

  onSubmit(): void {
    if (this.currentBuildingId && this.data) {
      this.loading = true;

      const payload = Object.fromEntries(
        this.fields.map(f => [f.key, this.data[f.key] ?? null])
      );

      this.shelterInformationService.updateShelterInformation(this.currentBuildingId, payload).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after updating Shelter Information.';
            this.loadData();
          } else {
            this.messageModal = 'unsuccess';
            this.popupMessage = response.message;
          }
        },
        error: (err) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
        }
      });
    }
  }

  // Toggle a Yes/No answer (model-driven so the active state is reliable).
  selectYesNo(key: keyof IShelterInformation, value: string): void {
    if (!this.canSave()) return;
    (this.data[key] as any) = value;
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }
}
